/**
 * Language Redux Helpers
 * ======================
 * Redux-dependent language utilities that need to be separate from languageCore
 * to avoid circular dependencies.
 *
 * Circular dependency chain that was broken:
 * store/index.ts → languageRouterMiddleware → languageCore → store/index.ts (CIRCULAR!)
 *
 * This file contains:
 * - syncLanguageFromUrl (server-side Redux thunk)
 * - syncLanguageFromUrlClient (client-side Redux sync)
 * - languageFromUrlMiddleware (SSR middleware)
 */

import { AppThunk } from "@Store/index";
import { updateUserLanguage } from "@App/appReducer";
import {
  extractLanguageFromPath,
  getLanguageFromHostname,
  isSupportedLanguage,
} from "./languageCore";

/**
 * Middleware to sync language from URL path to Redux store
 * Should be called on route changes
 */
export const syncLanguageFromUrl =
  ({ location }: any): AppThunk =>
  async (dispatch, getState) => {
    const currentLanguage = getState().appData.userLanguage;
    const pathLanguage = extractLanguageFromPath(location?.pathname || "");

    // If a valid language is found in the path and it's different from current, update it
    if (pathLanguage && pathLanguage !== currentLanguage) {
      dispatch(updateUserLanguage(pathLanguage));
    }
  };

/**
 * Client-side function to sync language from URL
 */
export const syncLanguageFromUrlClient = (
  pathname: string,
  dispatch: any,
  currentLanguage: string,
) => {
  const pathLanguage = extractLanguageFromPath(pathname);

  // If a valid language is found in the path and it's different from current, update it
  if (pathLanguage && pathLanguage !== currentLanguage) {
    dispatch(updateUserLanguage(pathLanguage));
  }
};

/**
 * Server-side middleware to detect language from URL path or domain only
 * Note: We intentionally do NOT use cookies for SSR language detection
 * to ensure the language is based on the URL the user is visiting
 */
export const languageFromUrlMiddleware =
  ({ req }: any): AppThunk =>
  async (dispatch) => {
    const pathname = req.path || req.url || "";
    const hostname = req.host || "";

    // Priority 1: Check URL path for language prefix (e.g., /fr/search)
    const pathLanguage = extractLanguageFromPath(pathname);
    if (pathLanguage && isSupportedLanguage(pathLanguage)) {
      dispatch(updateUserLanguage(pathLanguage));
      return;
    }

    // Priority 2: Check domain for language (e.g., amberstudent.cn = zh)
    const domainLanguage = getLanguageFromHostname(hostname);
    if (domainLanguage && isSupportedLanguage(domainLanguage)) {
      dispatch(updateUserLanguage(domainLanguage));
      return;
    }

    // Default: English
    dispatch(updateUserLanguage("en"));
  };
